// Sidebar map: the whole trace grouped by top-level branch, with review
// layers and coverage bars. Plus the header coverage readout.

import { branchCoverage, globalCoverage } from "../core/coverage";
import { jumpTo } from "../core/navigation";
import { reviewBranchesFrom, toggleReviewLayer } from "../core/reviews";
import { state, topBranchOf, type DecisionNode } from "../core/state";
import { truncate } from "../lib/text";
import { $, bar, el, setBar } from "./dom";
import { VERDICT_CLS } from "./card";

export function renderMap(): void {
  const sb = $("#mapList");
  sb.innerHTML = "";
  const root = state.rootId ? state.nodes.get(state.rootId) : null;
  if (!root) return;

  // review layers: one toggle per reviewer pass
  if (state.reviews.length) {
    const wrap = el("div", "layers");
    wrap.appendChild(el("div", "layers-title", "review layers"));
    state.reviews.forEach((l) => {
      const row = el("label", "layer-row");
      const cb = el("input") as HTMLInputElement;
      cb.type = "checkbox";
      cb.checked = l.enabled;
      cb.onchange = () => toggleReviewLayer(l.id);
      row.appendChild(cb);
      const n = l.events.filter((e) => e.t !== "summary").length;
      row.appendChild(el("span", "layer-txt", `${l.author} · ${n} annotation${n === 1 ? "" : "s"}`));
      if (l.status === "in-progress") row.appendChild(el("span", "layer-live", "● live"));
      wrap.appendChild(row);
    });
    sb.appendChild(wrap);
  }

  // root row
  sb.appendChild(mapRow(root));

  // group everything else by top-level branch
  if (root.chosenIndex != null || Object.keys(root.childId).length) {
    root.options.forEach((o) => {
      const label = o.label;
      const members = [...state.nodes.values()].filter((n) => n.depth > 0 && topBranchOf(n) === label);
      if (!members.length) return;
      const lbl = el("div", "map-branch-label");
      lbl.appendChild(document.createTextNode(truncate(label, 20)));
      lbl.appendChild(bar(branchCoverage(label)));
      sb.appendChild(lbl);
      members.sort((a, b) => a.depth - b.depth || a.id.localeCompare(b.id));
      members.forEach((n) => {
        sb.appendChild(mapRow(n));
        reviewBranchesFrom(n.id).forEach((b) => {   // reviewer forks render as ghosts
          const g = el("div", "map-ghost", `⑂ ${truncate(b.label, 34)}`);
          g.title = `${b.author} proposes an alternative line from “${truncate(n.question, 60)}” — open the node to see it`;
          g.onclick = () => jumpTo(n.id);
          sb.appendChild(g);
        });
      });
    });
  }
}

function mapRow(n: DecisionNode): HTMLElement {
  const isCurrent = n.id === state.currentId;
  const isOpen = n.chosenIndex == null;
  const row = el("div", "map-node" + (isCurrent ? " current" : "") + (isOpen ? " open" : ""));
  row.style.paddingLeft = (9 + Math.min(n.depth, 4) * 10) + "px";
  const q = el("div", "mn-q", truncate(n.depth === 0 ? "Where to start?" : n.question, 50));
  if (n.verdict) {
    const dot = el("span", "verdict-dot " + (VERDICT_CLS[n.verdict.verdict] || "v-sound"));
    dot.title = `review: ${n.verdict.verdict}${n.verdict.note ? " — " + n.verdict.note : ""}`;
    q.appendChild(dot);
  }
  row.appendChild(q);
  if (n.chosenIndex != null) {
    row.appendChild(el("div", "mn-chosen", truncate(n.options[n.chosenIndex]?.label || "", 40)));
  }
  row.onclick = () => jumpTo(n.id);
  return row;
}

export function renderCoverage(): void {
  const cov = globalCoverage();
  const barEl = document.getElementById("globalBar");
  if (barEl) setBar(barEl, cov);
  const pct = document.getElementById("globalPct");
  if (pct) pct.textContent = Math.round(cov * 100) + "%";
}
